import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { toast } from "react-toastify";
import {
    fetchAllReimbursement,
    fetchReimbursementMasters,
    syncReimbursementData,
    syncEligibility,
    fetchLTCEligibility,
    fetchReimbursementEligibility,
    deleteReimbursementRequest,
    clearError
} from "../../../core/data/redux/reimbursementSlice";
import { all_routes } from "../../router/all_routes";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { Spin, DatePicker, Select, Pagination, Modal, Table as AntTable } from "antd";
// import { formatCurrency } from "../../../utils/currencyUtils";
import moment from "moment";

// Inline currency formatter to avoid module resolution issues
const formatCurrency = (value: number | string | undefined): string => {
    if (value === undefined || value === null) return "₹ 0.00";
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return "₹ 0.00";
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numValue);
};

type AppDispatch = ThunkDispatch<any, any, AnyAction>;

const ReimbursementClaimsAdvances = () => {
    const routes = all_routes;
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const {
        requests,
        loading,
        pagination,
        reimbursementMasters,
        mastersLoading,
        error,
        reimbursementEligibility,
        ltcEligibility,
        eligibilityLoading
    } = useSelector((state: any) => state.reimbursementState);
    const { user } = useSelector((state: any) => state.authState);
    const empId = user?.data?.user?.empId || user?.empId || "00003865"; // Fallback to provided empId if not in state

    const [isSyncing, setIsSyncing] = useState(false);
    const [isLtcModalOpen, setIsLtcModalOpen] = useState(false);
    const [isEligibilityModalOpen, setIsEligibilityModalOpen] = useState(false);
    const [syncingEligibility, setSyncingEligibility] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{ id: number; ReferenceNumber?: string } | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        ReferenceNumber: "",
        ReimbursementType: "ALL",
        RequestType: "ALL",
        PhaseOftheRequest: "ALL",
        RequestStatus: "ALL",
        startDate: null as any,
        endDate: null as any,
    });

    useEffect(() => {
        dispatch(fetchReimbursementMasters());
        fetchData();
    }, [dispatch]);

    const handleDeleteClick = (request: any) => {
        setDeleteTarget({ id: request.id, ReferenceNumber: request.ReferenceNumber });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await dispatch(deleteReimbursementRequest({ id: deleteTarget.id, ReferenceNumber: deleteTarget.ReferenceNumber })).unwrap();
            toast.success("Request deleted successfully!");
            fetchData(); // Refresh list
        } catch (err) {
            toast.error("Failed to delete request. Please try again.");
        } finally {
            setDeleteLoading(false);
            setDeleteTarget(null);
        }
    };

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const fetchData = (page = 1, limit = 10) => {
        const payload: any = {
            page,
            limit,
            ReimbursementType: filters.ReimbursementType === "ALL" ? "" : filters.ReimbursementType,
            RequestStatus: filters.RequestStatus === "ALL" ? "" : filters.RequestStatus,
            RequestType: filters.RequestType === "ALL" ? "" : filters.RequestType,
        };

        if (filters.ReferenceNumber) payload.ReferenceNumber = filters.ReferenceNumber;
        if (filters.RequestType !== "ALL") payload.RequestType = filters.RequestType;
        else if (filters.RequestType === "ALL") payload.RequestType = "";

        if (filters.PhaseOftheRequest !== "ALL") payload.PhaseOftheRequest = filters.PhaseOftheRequest;
        else if (filters.PhaseOftheRequest === "ALL") payload.PhaseOftheRequest = "";

        if (filters.startDate) payload.startDate = filters.startDate.format("YYYY-MM-DD");
        if (filters.endDate) payload.endDate = filters.endDate.format("YYYY-MM-DD");

        dispatch(fetchAllReimbursement(payload));
    };

    const handleApplyFilters = () => {
        fetchData(1);
    };

    const handleClearFilters = () => {
        const cleared = {
            ReferenceNumber: "",
            ReimbursementType: "ALL",
            RequestType: "ALL",
            PhaseOftheRequest: "ALL",
            RequestStatus: "ALL",
            startDate: null,
            endDate: null,
        };
        setFilters(cleared);
        // Fetch with default (no filters)
        dispatch(fetchAllReimbursement({ page: 1, limit: 10, ReimbursementType: "", RequestStatus: "" }));
    };

    const getStatusHeaderColor = (status: string | undefined, secondStatus: string | undefined) => {
        const activeStatus = (status || secondStatus || "").toUpperCase();
        if (["A", "APPROVED", "S", "SETTLED"].includes(activeStatus)) return "#28a745"; // green
        if (["R", "REJECTED", "K", "CANCELLED", "X", "DELETED"].includes(activeStatus)) return "#dc3545"; // red
        if (["T", "P", "N", "PENDING", "TO BE APPROVED", "PARTIALLY APPROVED", "NEW"].includes(activeStatus)) return "#ffc107"; // orange/yellow
        if (["D", "PROCESSING COMPLETE", "C", "CLOSED"].includes(activeStatus)) return "#6f42c1"; // purple
        return "#6c757d";
    };

    const handleRefresh = async () => {
        setIsSyncing(true);
        try {
            await dispatch(syncReimbursementData(empId)).unwrap();
            toast.success("Data synced successfully");
            fetchData(1);
        } catch (err: any) {
            toast.error("Failed to sync data: " + err);
            // Still fetch existing data anyway
            fetchData(1);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleSyncEligibility = async (type: 'LTC' | 'REGULAR') => {
        setSyncingEligibility(true);
        try {
            await dispatch(syncEligibility(empId)).unwrap();
            toast.success("Eligibility synced from server");
            if (type === 'LTC') await dispatch(fetchLTCEligibility(empId)).unwrap();
            else await dispatch(fetchReimbursementEligibility(empId)).unwrap();
        } catch (err: any) {
            toast.error("Sync failed: " + err);
        } finally {
            setSyncingEligibility(false);
        }
    };

    const groupLtcData = (data: any[]) => {
        if (!data || !Array.isArray(data)) return [];

        // Sort by sequence number to ensure members follow schemes
        const sortedData = [...data].sort((a: any, b: any) =>
            (a.sequenceNumber || "").localeCompare(b.sequenceNumber || "")
        );

        const blocks: any[] = [];
        let currentBlock: any = null;

        sortedData.forEach(item => {
            if (item.scheme) {
                currentBlock = {
                    Scheme: item.scheme,
                    StartDate: item.startDate,
                    EndDate: item.endDate,
                    FamilyMembers: []
                };
                blocks.push(currentBlock);
            } else if (item.relationship || item.firstName) {
                if (currentBlock) {
                    currentBlock.FamilyMembers.push({
                        Relationship: item.relationship,
                        Name: item.firstName,
                        Eligible: item.isEligible ? 'Yes' : 'No'
                    });
                }
            }
        });

        return blocks;
    };

    const groupedLtc = groupLtcData(ltcEligibility);

    const openLtcModal = () => {
        setIsLtcModalOpen(true);
        dispatch(fetchLTCEligibility(empId));
    };

    const openEligibilityModal = () => {
        setIsEligibilityModalOpen(true);
        dispatch(fetchReimbursementEligibility(empId));
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";
        return moment(dateString).format("DD-MM-YYYY");
    };

    return (
        <div className="page-wrapper" style={{ backgroundColor: "#f8f9fa" }}>
            <div className="content">
                {/* Page Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                    <div className="my-auto mb-2">
                        <h3 className="page-title mb-1 fw-bold">Reimbursement Claims/Advances</h3>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={routes.adminDashboard}>Home</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="#">Services</Link>
                                </li>
                                <li className="breadcrumb-item active text-primary">Reimbursement</li>
                            </ol>
                        </nav>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            className={`btn btn-outline-primary rounded-pill px-3 shadow-sm border-2 ${isSyncing ? 'disabled' : ''}`}
                            onClick={handleRefresh}
                            disabled={isSyncing}
                        >
                            <i className={`ti ti-refresh me-1 ${isSyncing ? 'fa-spin' : ''}`}></i>
                            {isSyncing ? 'Syncing...' : 'Sync'}
                        </button>
                        <button
                            className="btn btn-primary rounded-pill px-4 shadow-sm fw-bold border-0"
                            style={{ backgroundColor: "#ab1f24" }}
                            onClick={() => setShowConfirmModal(true)}
                        >
                            <i className="ti ti-plus me-1"></i> Create New Request
                        </button>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div
                        className="card-header bg-white border-0 d-flex align-items-center justify-content-between py-3 cursor-pointer"
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                    >
                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                            <i className="ti ti-search me-2 text-primary" style={{ fontSize: '24px' }}></i>
                            Quick Criteria Maintenance
                        </h5>
                        <i className={`ti ti-chevron-${isFilterOpen ? 'up' : 'down'} fs-18 text-muted`}></i>
                    </div>
                    {isFilterOpen && (
                        <div className="card-body pt-0 px-4 pb-4">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label small text-muted fw-medium">Reference Number</label>
                                    <input
                                        type="text"
                                        className="form-control border-light-subtle rounded-3"
                                        placeholder="Enter Reference No"
                                        value={filters.ReferenceNumber}
                                        onChange={(e) => setFilters({ ...filters, ReferenceNumber: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted fw-medium">Reimbursement Type</label>
                                    <Select
                                        className="w-100 custom-select"
                                        value={filters.ReimbursementType}
                                        onChange={(val) => setFilters({ ...filters, ReimbursementType: val })}
                                        loading={mastersLoading}
                                        getPopupContainer={(trigger) => trigger.parentElement!}
                                    >
                                        <Select.Option value="ALL">All Reimbursement Types</Select.Option>
                                        {reimbursementMasters?.Reimbursement_Type?.map((m: any) => (
                                            <Select.Option key={m.type_id} value={m.type_id}>{m.type_text}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted fw-medium">Request Type</label>
                                    <Select
                                        className="w-100 custom-select"
                                        value={filters.RequestType}
                                        onChange={(val) => setFilters({ ...filters, RequestType: val })}
                                        loading={mastersLoading}
                                        getPopupContainer={(trigger) => trigger.parentElement!}
                                    >
                                        <Select.Option value="ALL">All Request Types</Select.Option>
                                        {reimbursementMasters?.Request_Type?.map((m: any) => (
                                            <Select.Option key={m.type_id} value={m.type_id}>{m.type_text}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted fw-medium">Request Phase</label>
                                    <Select
                                        className="w-100 custom-select"
                                        value={filters.PhaseOftheRequest}
                                        onChange={(val) => setFilters({ ...filters, PhaseOftheRequest: val })}
                                        loading={mastersLoading}
                                        getPopupContainer={(trigger) => trigger.parentElement!}
                                    >
                                        <Select.Option value="ALL">All Phases</Select.Option>
                                        {reimbursementMasters?.Request_Phase?.map((m: any) => (
                                            <Select.Option key={m.type_id} value={m.type_id}>{m.type_text}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted fw-medium">Request Status</label>
                                    <Select
                                        className="w-100 custom-select"
                                        value={filters.RequestStatus}
                                        onChange={(val) => setFilters({ ...filters, RequestStatus: val })}
                                        loading={mastersLoading}
                                        getPopupContainer={(trigger) => trigger.parentElement!}
                                    >
                                        <Select.Option value="ALL">All Status</Select.Option>
                                        {reimbursementMasters?.Request_Status?.map((m: any) => (
                                            <Select.Option key={m.type_id} value={m.type_id}>{m.type_text}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted fw-medium">Start Date</label>
                                    <DatePicker
                                        className="w-100 border-light-subtle rounded-3"
                                        value={filters.startDate}
                                        onChange={(date) => setFilters({ ...filters, startDate: date })}
                                        format="DD-MM-YYYY"
                                        placeholder="Start Date"
                                        getPopupContainer={(trigger) => trigger.parentElement!}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted fw-medium">End Date</label>
                                    <DatePicker
                                        className="w-100 border-light-subtle rounded-3"
                                        value={filters.endDate}
                                        onChange={(date) => setFilters({ ...filters, endDate: date })}
                                        format="DD-MM-YYYY"
                                        placeholder="End Date"
                                        getPopupContainer={(trigger) => trigger.parentElement!}
                                    />
                                </div>
                                <div className="col-md-3 d-flex align-items-end gap-2">
                                    <button
                                        className="btn btn-primary flex-grow-1 rounded-3 fw-bold"
                                        style={{ backgroundColor: "#ab1f24", borderColor: "#ab1f24" }}
                                        onClick={handleApplyFilters}
                                    >
                                        Apply
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary flex-grow-1 rounded-3 fw-bold"
                                        onClick={handleClearFilters}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Secondary Actions */}
                <div className="d-flex gap-3 mb-4">
                    <button
                        className="btn btn-white shadow-sm border-0 px-4 py-2 rounded-3 fw-medium d-flex align-items-center h-100"
                        onClick={openEligibilityModal}
                    >
                        <i className="ti ti-clipboard-list me-2 text-primary" style={{ fontSize: '22px' }}></i> Reimbursement Eligibility
                    </button>
                    <button
                        className="btn btn-white shadow-sm border-0 px-4 py-2 rounded-3 fw-medium d-flex align-items-center h-100"
                        onClick={openLtcModal}
                    >
                        <i className="ti ti-plane me-2 text-danger" style={{ fontSize: '22px' }}></i> LTC Eligibility
                    </button>
                </div>

                {/* List of Requests */}
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                        <Spin size="large" />
                    </div>
                ) : requests && requests.length > 0 ? (
                    <div className="row g-4">
                        {requests.map((request: any) => (
                            <div className="col-xl-4 col-lg-6 col-md-6" key={request.id}>
                                <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden position-relative hover-card mini-card" style={{ borderLeft: `5px solid ${getStatusHeaderColor(request.Request_Status, request.RequestStatus)}` }}>
                                    <div className="card-body p-3">
                                        {/* Header Row */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex flex-column">
                                                <span className="text-muted xxs text-uppercase fw-bold tracking-wider mb-1">Ref Number</span>
                                                <h6 className="fw-bold text-dark mb-0">{request.ReferenceNumber || "N/A"}</h6>
                                            </div>
                                            <span className="badge rounded-pill shadow-xs py-1 px-3 border" style={{ color: getStatusHeaderColor(request.Request_Status, request.RequestStatus), backgroundColor: 'white', borderColor: getStatusHeaderColor(request.Request_Status, request.RequestStatus) + '44' }}>
                                                {request.Request_Status || request.RequestStatus || "N/A"}
                                            </span>
                                        </div>

                                        {/* Content Row */}
                                        <div className="row g-2 mb-3">
                                            <div className="col-7">
                                                <div className="text-muted xxs text-uppercase fw-bold mb-1">Type</div>
                                                <div className="fw-bold text-dark small text-truncate" title={request.Reimbursement_Type || request.ReimbursementType}>
                                                    {request.Reimbursement_Type || request.ReimbursementType || "N/A"}
                                                </div>
                                            </div>
                                            <div className="col-5 text-end">
                                                <div className="text-muted xxs text-uppercase fw-bold mb-1">Creation Date</div>
                                                <div className="fw-bold text-dark small">{formatDate(request.created_at || request.RequestCreationDate)}</div>
                                            </div>
                                        </div>

                                        {/* Amounts Row */}
                                        {(() => {
                                            const reqVal = request.RequestedAmount === "0.00" ? (request.RequestedNumber || "0.00") : (request.RequestedAmount || "0.00");
                                            const appVal = request.ApprovedAmount === "0.00" ? (request.ApprovedNumber || "0.00") : (request.ApprovedAmount || "0.00");
                                            const isAmount = request.RequestedAmount !== "0.00" || request.ApprovedAmount !== "0.00" || (request.RequestedNumber === "0.00" && request.ApprovedNumber === "0.00");
                                            return (
                                                <div className="d-flex justify-content-between align-items-center py-2 px-3 mb-3 bg-light-subtle rounded-3">
                                                    <div>
                                                        <div className="xxs text-muted fw-bold text-uppercase">Requested</div>
                                                        <div className="fw-bold text-dark small">
                                                            {isAmount ? `₹${parseFloat(reqVal || "0").toLocaleString('en-IN')}` : reqVal}
                                                        </div>
                                                    </div>
                                                    <div className="vr mx-2 opacity-10"></div>
                                                    <div className="text-end">
                                                        <div className="xxs text-muted fw-bold text-uppercase">Approved</div>
                                                        <div className="fw-bold text-success small">
                                                            {isAmount ? `₹${parseFloat(appVal || "0").toLocaleString('en-IN')}` : appVal}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Footer Row */}
                                        <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                                            <div className="d-flex align-items-center">
                                                <div className="avatar avatar-xs bg-light rounded-circle me-2 d-flex align-items-center justify-content-center border" style={{ width: '28px', height: '28px' }}>
                                                    <i className="ti ti-user text-muted" style={{ fontSize: '12px' }}></i>
                                                </div>
                                                <div className="d-flex flex-column">
                                                    <span className="fw-bold text-dark small">ID: {formatEmpIdForDisplay(request.EmpID || "")}</span>
                                                    {request.ApproverUserID && (
                                                        <span className="text-muted fw-bold" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>APPROVER: {formatEmpIdForDisplay(request.ApproverUserID)}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                {/* Delete — visible only for N/T status (matching mobile) */}
                                                {(request.RequestStatus === "N" || request.RequestStatus === "T" ||
                                                    request.Request_Status === "N" || request.Request_Status === "T") && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-xs rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{ width: '30px', height: '30px', padding: 0 }}
                                                            onClick={() => handleDeleteClick(request)}
                                                            disabled={deleteLoading}
                                                            title="Delete Request"
                                                        >
                                                            <i className="ti ti-trash" style={{ fontSize: '14px' }}></i>
                                                        </button>
                                                    )}
                                                <Link
                                                    to={`/${routes.Reimbursement.details.replace(":id", request.ReferenceNumber)}`}
                                                    className="btn btn-danger btn-xs rounded-pill px-3 fw-bold"
                                                    style={{ backgroundColor: "#ab1f24", fontSize: '11px' }}
                                                >
                                                    View Details
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="card border-0 shadow-sm rounded-4 text-center py-5">
                        <div className="card-body">
                            <i className="ti ti-file-off fs-1 text-muted opacity-25 mb-3"></i>
                            <h4 className="fw-bold">No Records Found</h4>
                            <p className="text-muted px-4">There are no reimbursement requests matching your criteria.</p>
                            <button
                                className="btn btn-primary rounded-pill px-4 mt-2"
                                style={{ backgroundColor: "#ab1f24" }}
                                onClick={handleClearFilters}
                            >
                                Clear All Filters
                            </button>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {requests && requests.length > 0 && pagination && (
                    <div className="card-footer bg-white border-0 py-3 px-4 mt-2">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div className="text-muted small">
                                Showing {((pagination.page - 1) * (pagination.limit || 10)) + 1} - {Math.min(pagination.page * (pagination.limit || 10), pagination.total)} of {pagination.total} entries
                            </div>
                            <div className="custom-pagination">
                                <Pagination
                                    current={pagination.page}
                                    pageSize={pagination.limit || 10}
                                    total={pagination.total}
                                    onChange={(page) => fetchData(page)}
                                    showSizeChanger={false}
                                    itemRender={(page, type, originalElement) => {
                                        if (type === 'prev') {
                                            return <button className="btn btn-white btn-sm border-0"><i className="ti ti-chevron-left"></i></button>;
                                        }
                                        if (type === 'next') {
                                            return <button className="btn btn-white btn-sm border-0"><i className="ti ti-chevron-right"></i></button>;
                                        }
                                        return originalElement;
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* LTC Eligibility Modal */}
                <Modal
                    title={
                        <div className="d-flex align-items-center justify-content-between pe-4">
                            <span>LTC Eligibility</span>
                            <i className={`ti ti-refresh text-muted cursor-pointer ${syncingEligibility ? 'fa-spin' : ''}`} onClick={() => handleSyncEligibility('LTC')}></i>
                        </div>
                    }
                    open={isLtcModalOpen}
                    onCancel={() => setIsLtcModalOpen(false)}
                    footer={[
                        <button key="ok" className="btn btn-primary px-5 rounded-pill" style={{ backgroundColor: "#ab1f24", borderColor: "#ab1f24" }} onClick={() => setIsLtcModalOpen(false)}>OK</button>
                    ]}
                    width={1000}
                    zIndex={9999}
                    className="eligibility-modal"
                    centered
                >
                    <div className="modal-body p-0">
                        <div className="text-center mb-3">
                            <button
                                className="btn btn-sm btn-light border rounded-pill px-3 py-1 shadow-sm d-inline-flex align-items-center"
                                onClick={() => handleSyncEligibility('LTC')}
                                disabled={syncingEligibility}
                            >
                                <i className={`ti ti-refresh me-1 ${syncingEligibility ? 'fa-spin' : ''}`}></i>
                                <span className="small fw-bold text-secondary">Sync Eligibility</span>
                            </button>
                        </div>

                        <div className="eligibility-container custom-scrollbar px-1" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {eligibilityLoading && !syncingEligibility ? (
                                <div className="text-center py-5"><Spin /></div>
                            ) : groupedLtc?.length > 0 ? (
                                <div className="row">
                                    {groupedLtc.map((item: any, index: number) => (
                                        <div key={index} className="col-lg-6">
                                            <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden" style={{ borderLeft: '4px solid #ab1f24' }}>
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div className="bg-danger-transparent p-2 rounded-3 me-3">
                                                            <i className="ti ti-map-2 text-danger fs-3"></i>
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-0 fw-bold">{item.Scheme}</h6>
                                                            <small className="text-muted fw-medium">{item.StartDate} - {item.EndDate}</small>
                                                        </div>
                                                    </div>

                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <thead className="bg-light-subtle small fw-bold text-muted border-bottom">
                                                                <tr>
                                                                    <th>Relationship</th>
                                                                    <th>Name</th>
                                                                    <th className="text-end">Eligible</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="small">
                                                                {item.FamilyMembers?.map((member: any, midx: number) => (
                                                                    <tr key={midx}>
                                                                        <td className="text-capitalize">{member.Relationship}</td>
                                                                        <td className="fw-medium">{member.Name}</td>
                                                                        <td className="text-end">
                                                                            <span className={`badge rounded-pill ${member.Eligible === 'Yes' ? 'bg-success-transparent text-success' : 'bg-danger-transparent text-danger'}`}>
                                                                                {member.Eligible}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                {(!item.FamilyMembers || item.FamilyMembers.length === 0) && (
                                                                    <tr>
                                                                        <td colSpan={3} className="text-center py-2 text-muted italic">No family members listed</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="ti ti-info-circle text-muted fs-1 opacity-25 mb-2 d-block"></i>
                                    <p className="text-muted small">No LTC eligibility records found.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>

                {/* Reimbursement Eligibility Modal */}
                <Modal
                    title={
                        <div className="d-flex align-items-center justify-content-between pe-4">
                            <span>Reimbursement Eligibility</span>
                            <i className={`ti ti-refresh text-muted cursor-pointer ${syncingEligibility ? 'fa-spin' : ''}`} onClick={() => handleSyncEligibility('REGULAR')}></i>
                        </div>
                    }
                    open={isEligibilityModalOpen}
                    onCancel={() => setIsEligibilityModalOpen(false)}
                    footer={[
                        <button key="ok" className="btn btn-primary px-5 rounded-pill" style={{ backgroundColor: "#ab1f24", borderColor: "#ab1f24" }} onClick={() => setIsEligibilityModalOpen(false)}>OK</button>
                    ]}
                    width={1000}
                    zIndex={9999}
                    className="eligibility-modal"
                    centered
                >
                    <div className="modal-body p-0">
                        <div className="text-center mb-3">
                            <button
                                className="btn btn-sm btn-light border rounded-pill px-3 py-1 shadow-sm d-inline-flex align-items-center"
                                onClick={() => handleSyncEligibility('REGULAR')}
                                disabled={syncingEligibility}
                            >
                                <i className={`ti ti-refresh me-1 ${syncingEligibility ? 'fa-spin' : ''}`}></i>
                                <span className="small fw-bold text-secondary">Sync Eligibility</span>
                            </button>
                        </div>

                        <div className="eligibility-container custom-scrollbar px-1" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {eligibilityLoading && !syncingEligibility ? (
                                <div className="text-center py-5"><Spin /></div>
                            ) : reimbursementEligibility?.length > 0 ? (
                                <div className="row">
                                    {reimbursementEligibility.map((item: any, index: number) => (
                                        <div key={index} className="col-lg-6">
                                            <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden" style={{ borderLeft: '4px solid #ab1f24' }}>
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-center mb-3">
                                                        <div className="bg-danger-transparent p-2 rounded-3 me-3">
                                                            <i className="ti ti-wallet text-danger fs-3"></i>
                                                        </div>
                                                        <h6 className="mb-0 fw-bold">{item.reimbursement_type_name || item.reimbursement_text}</h6>
                                                    </div>

                                                    <div className="row g-2 mb-3">
                                                        <div className="col-6">
                                                            <label className="small text-muted mb-0">Start Date</label>
                                                            <div className="fw-medium small">{item.eligibility_start_date}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <label className="small text-muted mb-0 text-end d-block">End Date</label>
                                                            <div className="fw-medium small text-end">{item.eligibility_end_date}</div>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-center p-2 bg-light-subtle rounded-3 mb-2">
                                                        <span className="small text-muted fw-medium">Eligible Value</span>
                                                        <span className="fw-bold text-success">{formatCurrency(item.eligible_value || 0)}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center p-2 bg-light-subtle rounded-3">
                                                        <span className="small text-muted fw-medium">Balance Value</span>
                                                        <span className="fw-bold text-success">{formatCurrency(item.balance_value || 0)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="ti ti-info-circle text-muted fs-1 opacity-25 mb-2 d-block"></i>
                                    <p className="text-muted small">No reimbursement eligibility records found.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>

                <style>{`
                .custom-select .ant-select-selector {
                    border-radius: 8px !important;
                    border-color: #dee2e6 !important;
                    height: 38px !important;
                    display: flex;
                    align-items: center;
                }
                .xxs { font-size: 10px; }
                .letter-spacing-1 { letter-spacing: 0.5px; }
                .hover-card { transition: transform 0.2s, shadow 0.2s; }
                .hover-card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important; }
                .cursor-pointer { cursor: pointer; }
                .btn-white { background-color: #fff; color: #444; }
                .btn-white:hover { background-color: #f8f9fa; }
                
                /* Custom Pagination Styles */
                .custom-pagination .ant-pagination {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }
                .custom-pagination .ant-pagination-item {
                    border: none;
                    background: transparent;
                    min-width: 32px;
                    height: 32px;
                    line-height: 32px;
                    border-radius: 50%;
                }
                .custom-pagination .ant-pagination-item-active {
                    background-color: #ab1f24 !important;
                    border-color: #ab1f24 !important;
                }
                .custom-pagination .ant-pagination-item-active a {
                    color: #fff !important;
                }
                .custom-pagination .ant-pagination-prev, 
                .custom-pagination .ant-pagination-next {
                    min-width: 32px;
                    height: 32px;
                    line-height: 32px;
                }
                .custom-pagination .ant-pagination-disabled .btn-white {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                /* Fix for DatePicker header icon overlapping/backslash issue */
                .ant-picker-header-super-prev-btn::before,
                .ant-picker-header-prev-btn::before,
                .ant-picker-header-next-btn::before,
                .ant-picker-header-super-next-btn::before {
                    content: none !important;
                    display: none !important;
                }
                .ant-picker-header-super-prev-btn,
                .ant-picker-header-prev-btn,
                .ant-picker-header-next-btn,
                .ant-picker-header-super-next-btn {
                    min-width: 1.6em !important;
                    font-size: 14px !important;
                }
                .fa-spin {
                    animation: fa-spin 2s infinite linear;
                }
                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(359deg); }
                }
            `}</style>
            </div>
            <Modal
                title={<h5 className="mb-0 fw-bold">Confirmation</h5>}
                open={showConfirmModal}
                onOk={() => {
                    setShowConfirmModal(false);
                    const params = new URLSearchParams();
                    if (filters.ReimbursementType && filters.ReimbursementType !== "ALL") {
                        params.append("reimbursementType", filters.ReimbursementType);
                    }
                    if (filters.RequestType && filters.RequestType !== "ALL") {
                        params.append("requestType", filters.RequestType);
                    }
                    const queryString = params.toString();
                    navigate(`/${routes.Reimbursement.create}${queryString ? `?${queryString}` : ""}`);
                }}
                onCancel={() => setShowConfirmModal(false)}
                okText="Yes, Proceed"
                cancelText="No, Go Back"
                centered
                maskClosable={false}
            >
                <div className="py-3 text-center">
                    <i className="ti ti-question-mark fs-48 text-warning mb-3"></i>
                    <p className="fs-16 text-dark">
                        Are you sure you want to create a reimbursement request?
                    </p>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                title={<h5 className="mb-0 fw-bold text-danger"><i className="ti ti-trash me-2"></i>Delete Request</h5>}
                open={deleteTarget !== null}
                onOk={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
                okText={deleteLoading ? "Deleting..." : "Yes, Delete"}
                cancelText="Cancel"
                centered
                maskClosable={!deleteLoading}
                okButtonProps={{ danger: true, loading: deleteLoading }}
            >
                <div className="py-3 text-center">
                    <i className="ti ti-alert-triangle fs-48 text-danger mb-3" style={{ fontSize: '48px' }}></i>
                    <p className="fs-16 text-dark mt-3">
                        Are you sure you want to delete request <strong>{deleteTarget?.ReferenceNumber}</strong>?
                    </p>
                    <p className="text-muted small">This action cannot be undone.</p>
                </div>
            </Modal>
        </div>
    );
};

export default ReimbursementClaimsAdvances;
